import dayjs from 'dayjs';
import { ReservationActionTypesEnum, ReservationFlagTypeEnum, ReservationsDataOutDTO } from '@api/ringil-api.v2';

const PREVIEW_TZ = 'Europe/Prague';
const PREVIEW_START_LOCAL = '2026-08-05T08:00:00';
const PREVIEW_START_UTC = '2026-08-05T06:00:00Z';

/**
 * Preview data for the layout designer.
 *
 * Every field the fragment registry can render is populated, so no fragment ever renders empty in
 * the preview and the designer shows what each one actually looks like. It is deliberately static
 * (no API call): the designer must work regardless of what data happens to exist in the backend.
 *
 * `customerId` is intentionally a value no real company has, so the detail's workflow-state
 * fragment resolves to read-only and skips its transition fetch.
 */
export const MOCK_RESERVATION: ReservationsDataOutDTO = {
  reservationId: 'preview-reservation',
  reservationApplicationId: 'RES-2026-0042',
  version: 1,
  customerId: 'preview-customer',
  customerName: 'Alpina Foods s.r.o.',
  carrierId: 'preview-carrier',
  carrierName: 'Rohlík Transport',
  locationId: 'preview-location',
  locationName: 'Sklad Praha–Horní Počernice',
  calendarId: 'preview-calendar',
  rampId: 'preview-ramp',
  timezoneId: 'Europe/Prague',
  timeslot: {
    // `since` / `till` are deprecated on the DTO but still required; kept consistent with the UTC pair.
    since: PREVIEW_START_LOCAL,
    till: '2026-08-05T08:30:00',
    utcStart: PREVIEW_START_UTC,
    utcEnd: '2026-08-05T06:30:00Z',
    timezoneId: PREVIEW_TZ,
  },
  carPlate: '5AB 1234',
  driverInfo: {
    driverId: 'preview-driver',
    driverName: 'Martin Dvořák',
    driverPhone: '+420 601 234 567',
  },
  order: {
    orderId: 'OBJ-88213',
    orderNote: 'Chladírenský vůz, teplota 2–6 °C',
    reservationName: 'Závoz Horní Počernice',
  },
  orders: [
    {
      id: 'preview-order-1',
      orderName: 'OBJ-88213',
      orderNote: 'Chladírenský vůz, teplota 2–6 °C',
      orderAmount: 12,
      orderTotalWeight: 8420,
      totalVolume: 21.5,
      items: [
        {
          id: 'preview-item-1',
          cargoTemplate: { name: 'EUR pallet', code: 'EUR', decimalPlaces: 0 },
          stackable: true,
          amount: 12,
          height: 1.2,
          width: 0.8,
          length: 1.2,
          weight: 8420,
        },
      ],
    },
  ],
  labels: [
    { labelId: 'preview-label-1', labelValue: 'Urgentní', labelColor: '#FE5000' },
    { labelId: 'preview-label-2', labelValue: 'Chlazené', labelColor: '#2F80ED' },
  ],
  createdAt: '2026-07-28T09:14:00Z',
  createdByFullName: 'Jana Nováková',
  updatedByFullName: 'Petr Svoboda',
  updatedAt: '2026-08-01T13:02:00Z',
  state: {
    name: 'processing',
    icon: { name: 'play', color: 'complementary-processing' },
    transitions: [],
    orderIndex: 2,
  },
  isPinnedOnLocRampTable: false,
  reservationName: 'Závoz Horní Počernice',
  reservationActionTypes: [ReservationActionTypesEnum.LOADING, ReservationActionTypesEnum.UNLOADING],
  flags: [
    { reservationFlagType: ReservationFlagTypeEnum.RETURNABLE_PACKAGE, units: 12 },
    { reservationFlagType: ReservationFlagTypeEnum.CUSTOMS },
  ],
  onBehalfOf: { id: 'preview-obo', externalId: 'OBO-01', name: 'Kaufland Česká republika v.o.s.' },
  anonymous: false,
  linkedToExpCoords: {
    applicationId: 'EXP-2026-1177',
    expeditionId: 'preview-expedition',
    isCarrier: false,
    customerId: 'preview-customer',
  },
  linkedSupplierNotification: {
    supplierNotificationId: 'preview-sn',
    applicationId: 'SN-2026-0310',
    supplierCompanyId: 'preview-supplier',
    supplierCompanyName: 'Beskyd Fryčovice a.s.',
  },
};

/**
 * The preview reservation at a given length.
 *
 * The card styles itself from the reservation's own duration — under 30 minutes it switches to the
 * tighter `pt-1 pb-0` padding — so previewing a 15-minute slot means actually handing the component
 * a 15-minute reservation, not just shrinking the box around it.
 */
export const previewReservation = (minutes: number): ReservationsDataOutDTO => {
  const localStart = dayjs(PREVIEW_START_LOCAL);
  const utcStart = dayjs(PREVIEW_START_UTC);
  return {
    ...MOCK_RESERVATION,
    timeslot: {
      since: localStart.format('YYYY-MM-DDTHH:mm:ss'),
      till: localStart.add(minutes, 'minute').format('YYYY-MM-DDTHH:mm:ss'),
      utcStart: utcStart.toISOString(),
      utcEnd: utcStart.add(minutes, 'minute').toISOString(),
      timezoneId: PREVIEW_TZ,
    },
  };
};
